import { api } from '@xtnd/web/shared/api';

/**
 * Request interface for template testing
 */
export interface TemplateTestRequest {
  templateId: string;
  sampleData: Record<string, any>;
}

/**
 * Response interface for template testing
 */
export interface TemplateTestResponse {
  success: boolean;
  templateName?: string;
  templateId?: string;
  rendered: string;
  variables: string[];
  downloadUrl?: string;
  templateType?: string;
}

/**
 * Request interface for email sending
 */
export interface SendEmailRequest {
  templateId: string;
  sampleData: Record<string, any>;
  toEmail: string;
  subject: string;
  attachment?: File;
  secureAttachment: boolean;
}

/**
 * Response interface for email sending
 */
export interface SendEmailResponse {
  message: string;
  details: {
    to: string;
    subject: string;
    messageId?: string;
    emailLogUrl?: string;
    hasAttachment?: boolean;
    secureAttachment?: boolean;
  };
}

/**
 * API service for template testing and email operations
 */
class TemplateTestingApi {
  /**
   * Test template rendering with sample data
   * @param request - Template ID and sample data
   * @returns Promise with rendered template
   */
  async testTemplate(request: TemplateTestRequest): Promise<TemplateTestResponse> {
    return api.post<TemplateTestResponse>('/templates/test', request);
  }

  /**
   * Send test email using template
   * @param request - Email data including template ID, recipient, and content
   * @returns Promise with email sending result
   */
  async sendTestEmail(request: SendEmailRequest): Promise<SendEmailResponse> {
    const formData = new FormData();
    formData.append('templateId', request.templateId);
    formData.append('sampleData', JSON.stringify(request.sampleData));
    formData.append('toEmail', request.toEmail);
    formData.append('subject', request.subject);
    formData.append('secureAttachment', request.secureAttachment.toString());

    if (request.attachment) {
      formData.append('attachment', request.attachment);
    }

    return api.post<SendEmailResponse>('/templates/send-email', formData);
  }
}

export const templateTestingApi = new TemplateTestingApi();

